import React from 'react';
import Skeleton from './Skeleton';

/** Label bar + input block — the shape every field in the form takes. */
const FieldBlock = ({ labelWidth = 'w-24' }) => (
  <div>
    <Skeleton className={`mb-1.5 h-2.5 rounded ${labelWidth}`} />
    <Skeleton className="h-11 w-full rounded-lg" />
  </div>
);

/**
 * Shimmer placeholder for the Add / Edit Care Package modal.
 *
 * Follows the real form's rhythm — name, the boxed service-user panel, the
 * paired zone/date rows, the four-up visit block and the tasks panel — so the
 * dialog doesn't resize under the cursor when the data arrives.
 */
const PackageFormSkeleton = () => (
  <div className="flex-1 space-y-5 overflow-y-auto px-6 py-5">
    {/* Care package name */}
    <FieldBlock labelWidth="w-32" />

    {/* Service user — boxed panel */}
    <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-gray-50/60 dark:bg-[#20242c] p-3">
      <Skeleton className="mb-1.5 h-2.5 w-20 rounded" />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Skeleton className="h-11 w-full rounded-lg" />
        <Skeleton className="h-11 w-full rounded-lg" />
      </div>
    </div>

    {/* Travel zone + travel area */}
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <FieldBlock labelWidth="w-20" />
      <FieldBlock labelWidth="w-20" />
    </div>

    {/* Start / end date */}
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <FieldBlock labelWidth="w-16" />
      <FieldBlock labelWidth="w-28" />
    </div>

    {/* Visit block — type / start / end / duration */}
    <div>
      <Skeleton className="mb-1.5 h-2.5 w-16 rounded" />
      <div className="grid grid-cols-2 gap-3 rounded-xl border border-gray-200 dark:border-gray-800 bg-gray-50/60 dark:bg-[#20242c] p-3 sm:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i}>
            <Skeleton className="mb-1.5 h-2.5 w-12 rounded" />
            <Skeleton className="h-11 w-full rounded-lg" />
          </div>
        ))}
      </div>
    </div>

    {/* Tasks */}
    <div>
      <Skeleton className="mb-1.5 h-2.5 w-14 rounded" />
      <div className="space-y-2 rounded-xl border border-gray-200 dark:border-gray-800 bg-gray-50/60 dark:bg-[#20242c] p-3">
        {[0, 1, 2].map((i) => (
          <div key={i} className="flex items-center gap-2">
            <Skeleton className="h-4 w-4 shrink-0 rounded" />
            {/* Uneven widths so it reads as a task list, not identical bars. */}
            <Skeleton className={`h-9 rounded-lg ${['w-full', 'w-4/5', 'w-2/3'][i]}`} />
          </div>
        ))}
        <Skeleton className="h-9 w-32 rounded-lg" />
      </div>
    </div>

    {/* Preferred carers + initial assignment */}
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <FieldBlock labelWidth="w-28" />
      <FieldBlock labelWidth="w-28" />
    </div>
  </div>
);

export default PackageFormSkeleton;
